import { runQualityCheck } from '@/services/application';
import { BASEURL } from '@/services/network';
import { logError, logger } from '@/utils/usefulFuncs';
import { createAuthedEventSource } from '@/utils/streamAuth';

export class StreamController {
  constructor(resultId, options, ref, dispatch, REDUCER_ACTIONS, isAgentScriptResult = false) {
    this._isCanceled = false;

    this._errorRetryTimeout = null;
    this._errorRetries = 0;
    this._errorRetryInterval = 1000;
    this._errorRetryMax = 3;

    this._pendingAnswer = null;
    this._throttleTimer = null;
    this._throttleInterval = 250;

    this.resultId = resultId;
    this.options = options || {};
    this.streamHeaders = this.options.headers || {};
    this.enableQualityCheck = this.options.enableQualityCheck || false;
    this.includeContext = this.options.includeContext !== false;
    this.includeAgentPlan = this.options.includeAgentPlan ?? isAgentScriptResult;
    this.ref = ref;
    this.dispatch = dispatch;
    this.REDUCER_ACTIONS = REDUCER_ACTIONS;
    this.isAgentScriptResult = isAgentScriptResult;

    if (resultId) {
      this._create();
      return this.ref;
    } else {
      return null;
    }
  }

  _buildUrl = () => {
    const params = [`run_id=${encodeURIComponent(this.resultId)}`];
    if (this.includeContext) params.push('include_context=true');
    if (this.includeAgentPlan) params.push('include_agent_plan=true');
    return `${BASEURL}/api/v2/genai/queries/stream_events?${params.join('&')}`;
  };

  _create = () => {
    try {
      if (this._isCanceled) {
        logger('StreamController _create', 'Stream is canceled', this);
        return;
      }

      const URL = this._buildUrl();
      const source = createAuthedEventSource(URL, { headers: this.streamHeaders });
      this._setStreamSource(source);

      source.addEventListener('open', this._onOpen);
      source.addEventListener('message', this._onMessage);
      source.addEventListener('error', this._onError);
      source.addEventListener('refining', this._onRefiningStart);
      source.addEventListener('complete', this._onComplete);
    } catch (error) {
      logError('StreamController _create', error);
      this._onError(error);
    }
  };

  destroy = (isCanceled = false) => {
    this._isCanceled = isCanceled;
    this._flushPendingAnswer();
    if (this._errorRetryTimeout) {
      clearTimeout(this._errorRetryTimeout);
      this._errorRetryTimeout = null;
    }
    if (this._getStreamSource()) {
      this._getStreamSource().close();
      this._setStreamSource(null);
    }
  };

  _onOpen = () => {
    if (this._isCanceled) {
      return;
    }
    this.dispatch({ type: this.REDUCER_ACTIONS.open, payload: this.resultId });
  };

  _onMessage = (event) => {
    if (this._isCanceled) {
      return;
    }
    const { data } = event;
    const streamData = JSON.parse(data);
    const { answer } = streamData;
    logger('StreamController _onMessage', 'received', {
      runId: this.resultId,
      questionIndex: streamData?.question_index,
      finalAnswer: streamData?.final_answer === true,
      answerLength: answer?.length || 0,
    });
    if (answer?.length > 0) {
      this._pendingAnswer = answer;
      if (!this._throttleTimer) {
        this._throttleTimer = setTimeout(() => {
          this._throttleTimer = null;
          if (this._pendingAnswer !== null) {
            this.dispatch({ type: this.REDUCER_ACTIONS.messageReceived, payload: this._pendingAnswer });
          }
        }, this._throttleInterval);
      }
    }
  };

  _flushPendingAnswer = () => {
    if (this._throttleTimer) {
      clearTimeout(this._throttleTimer);
      this._throttleTimer = null;
    }
    if (this._pendingAnswer !== null) {
      this.dispatch({ type: this.REDUCER_ACTIONS.messageReceived, payload: this._pendingAnswer });
      this._pendingAnswer = null;
    }
  };

  _onError = (error) => {
    if (this._isCanceled) {
      return;
    }
    if (!error.error) {
      return;
    }

    logger('StreamController _onError', error);
    this.dispatch({ type: this.REDUCER_ACTIONS.errorReceived, payload: error });
    this.destroy(true);

    if (this._errorRetries < this._errorRetryMax) {
      this._errorRetries++;
      const timeout = setTimeout(() => {
        this._create();
      }, this._errorRetryInterval);
      this._errorRetryTimeout = timeout;
    }
  };

  _onRefiningStart = () => {
    if (this._isCanceled) {
      return;
    }
    this.dispatch({ type: this.REDUCER_ACTIONS.refiningStart });
  };

  _onComplete = (event) => {
    if (this._isCanceled) {
      return;
    }
    this._flushPendingAnswer();
    const { data } = event;
    const streamData = JSON.parse(data);
    console.log('StreamController complete payload', streamData);
    const { response } = streamData;
    const answers = response?.answers || [];
    const row = answers.find((r) => r?.final_answer) || answers[0];
    if (!row) {
      logError('StreamController _onComplete', 'No answers in response');
      this.destroy(true);
      return;
    }
    const { answer, query_run_id: queryRunId } = row;
    if (row.kind === 'agent') {
      logger('StreamController _onComplete', 'agent result', { row });
      this.dispatch({
        type: this.REDUCER_ACTIONS.complete,
        payload: { answer, queryRunId, kind: row.kind, agentExecutedPlan: row.agent_executed_plan },
      });
      this.destroy(true);
      return;
    }
    if (typeof answer !== 'string' || answer.length === 0) {
      return;
    }
    const result = {
      answer,
      queryRunId,
      kind: row.kind,
      context: row.context,
      fullContext: row.context_with_all_headers,
      sources: row.sources,
    };
    logger('StreamController _onComplete', 'query result', { row });
    this.dispatch({ type: this.REDUCER_ACTIONS.complete, payload: result });

    if (this.enableQualityCheck && queryRunId) {
      runQualityCheck(queryRunId);
    }
    this.destroy(true);
  };

  _getStreamSource = () => {
    return this.ref.current;
  };

  _setStreamSource = (source) => {
    this.ref.current = source;
  };
}
